import { isNonNullable, isNullable } from '@ipfabric/utils';
import { List } from 'immutable';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import { merge, pipe } from 'ramda';
import wordWrap from 'word-wrap';

export function getScoreChart(
  Chart,
  canvas,
  title,
  animationEnabled,
  labels,
  data,
  instance,
  absolute = false,
) {
  const colors = {
    backgroundColor: {
      ok: '#70AD47',
      success: '#70AD47',
      info: '#4F81BD',
      warning: '#FFBF13',
      critical: '#FF2616',
      danger: '#FF2616',
    },
    hoverBackgroundColor: {
      ok: '#65AD36',
      success: '#65AD36',
      info: 'rgba(79, 129, 189, 0.8)',
      warning: '#FFC000',
      critical: '#FF0000',
      danger: '#FF0000',
    },
  };
  const dataKeys = ['ok', 'info', 'warning', 'critical'].filter((key) =>
    isNonNullable(data[key]),
  );
  const chartData = {
    labels: [],
    datasets: dataKeys.map((key) => {
      const rtn = {};
      rtn.label =
        isNonNullable(data[key]) && isNonNullable(labels[key])
          ? `${data[key]}${absolute === false ? '%' : ''} ${labels[key]}`
          : '                          ';
      rtn.data = [isNonNullable(data[key]) ? data[key] : 0];
      rtn.backgroundColor = isNonNullable(data[key])
        ? colors.backgroundColor[key]
        : 'rgba(0,0,0,0)';
      rtn.hoverBackgroundColor = isNonNullable(data[key])
        ? colors.hoverBackgroundColor[key]
        : 'rgba(0,0,0,0)';
      return rtn;
    }),
  };

  if (instance) {
    instance.destroy();
  }
  const splitTitle = wordWrap(title, {
    width: 10,
    trim: true,
    indent: '',
  }).split('\n');
  return new Chart(canvas, {
    type: 'bar',
    data: chartData,
    animation: animationEnabled,
    options: {
      animation: {
        duration: animationEnabled ? 400 : 0,
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            gridLines: { display: false },
          },
        ],
        yAxes: [
          {
            ticks: {
              callback: (value) => `${value}${absolute === false ? '%' : ''}`,
            },
            stacked: true,
            gridLines: { display: false },
          },
        ],
      },
      title: {
        display: true,
        text: splitTitle,
        padding: 20,
        fontSize: 18,
        fontColor: '#000',
      },
      tooltips: {
        mode: 'label',
        callbacks: {
          title: () => '',
          label: (tooltipItem, labelData) =>
            labelData.datasets[tooltipItem.datasetIndex].label.trim().length > 0
              ? labelData.datasets[tooltipItem.datasetIndex].label
              : null,
        },
      },
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          generateLabels: (chart) =>
            chart.config.data.datasets.map((s) => ({
              text: s.label,
              fillStyle: s.backgroundColor,
              lineWidth: 0,
              strokeStyle: 'rgba(0,0,0,0)',
              hidden: false,
            })),
        },
        onClick: () => {},
      },
      responsive: false,
      maintainAspectRatio: false,
    },
  });
}

export function getHalfDonutChart(
  Chart,
  canvas,
  title,
  animationEnabled,
  labels,
  data,
  instance,
  absolute = false,
) {
  const colors = {
    backgroundColor: {
      ok: '#70AD47',
      info: '#5B9BD5',
      warning: '#FFBF13',
      critical: '#FF2616',
    },
    hoverBackgroundColor: {
      ok: '#65AD36',
      info: '#4894D5',
      warning: '#FFC000',
      critical: '#FF0000',
    },
  };
  const dataKeys = ['ok', 'info', 'warning', 'critical'];
  dataKeys.sort((a) => isNullable(data[a]));

  const pieData = {
    labels: dataKeys.map((key) =>
      isNonNullable(data[key]) && isNonNullable(labels[key])
        ? `${data[key]}${absolute === false ? '%' : ''} ${labels[key]}    `
        : '                          ',
    ),
    datasets: [
      {
        data: dataKeys.map((key) => (isNonNullable(data[key]) ? data[key] : 0)),
        backgroundColor: dataKeys.map((key) =>
          isNonNullable(data[key])
            ? colors.backgroundColor[key]
            : 'rgba(0,0,0,0)',
        ),
        hoverBorderColor: '#666',
        hoverBackgroundColor: dataKeys.map((key) =>
          isNonNullable(data[key])
            ? colors.hoverBackgroundColor[key]
            : 'rgba(0,0,0,0)',
        ),
      },
    ],
  };

  if (instance && isFunction(instance.destroy)) {
    instance.destroy();
  }
  const splitTitle = wordWrap(title, {
    width: 16,
    trim: true,
    indent: '',
  }).split('\n');
  return new Chart(canvas, {
    type: 'doughnut',
    data: pieData,
    animation: animationEnabled,
    options: {
      showTextInMiddle: true,
      absolute,
      animation: {
        duration: animationEnabled ? 1500 : 0,
      },
      elements: {
        arc: {
          borderWidth: 0,
        },
      },
      title: {
        display: true,
        text: splitTitle,
        padding: 0,
        fontSize: 18,
        fontColor: '#000',
      },
      tooltips: {
        bodySpacing: 5,
        callbacks: {
          label: (tooltipItem, labelData) => {
            const { index } = tooltipItem;
            return labelData.labels[index];
          },
        },
      },
      legend: {
        position: 'bottom',
        fullWidth: true,
        labels: {
          boxWidth: 12,
        },
        onClick: () => {},
      },
      circumference: Math.PI,
      rotation: Math.PI,
      responsive: false,
      maintainAspectRatio: false,
    },
  });
}

export function getPieVendorChart(
  Chart,
  canvas,
  title,
  animationEnabled,
  vectorLabels,
  vectorData,
) {
  const data = vectorData.slice(0, 7);
  const labels = vectorLabels.slice(0, 7);
  const colors = {
    backgroundColor: [
      '#3F6D98',
      '#4A7FB0',
      '#538EC3',
      '#5B9BD5',
      '#8BB1DD',
      '#ADC6E5',
      '#C7D7EC',
    ],
    hoverBackgroundColor: [
      '#3F6D98',
      '#4A7FB0',
      '#538EC3',
      '#5B9BD5',
      '#8BB1DD',
      '#ADC6E5',
      '#C7D7EC',
    ],
  };

  const pieData = {
    labels: labels.map((l, i) => `${l} ${data[i]}%`),
    datasets: [
      {
        data,
        backgroundColor: colors.backgroundColor.slice(0, data.length),
        hoverBorderColor: '#666',
        hoverBackgroundColor: colors.hoverBackgroundColor.slice(0, data.length),
      },
    ],
  };

  return new Chart(canvas, {
    type: 'pie',
    data: pieData,
    animation: animationEnabled,
    options: {
      showPercentage: true,
      title: {
        display: true,
        text: title,
        padding: 0,
        fontSize: 18,
        fontColor: '#000',
      },
      tooltips: {
        bodySpacing: 5,
        callbacks: {
          label: (tooltipItem, labelData) => {
            const { index } = tooltipItem;
            return labelData.labels[index];
          },
        },
      },
      legend: {
        position: 'bottom',
        fullWidth: true,
        labels: {
          boxWidth: 12,
        },
        onClick: () => {},
      },
      animation: {
        duration: animationEnabled ? 1500 : 0,
        animateScale: true,
        animateRotate: true,
      },
      responsive: false,
    },
  });
}

export function getSnapshotChangesChart(
  Chart,
  canvas,
  msg,
  animationEnabled,
  labels,
  types,
  titles,
  data,
  colors,
  hoverColors,
  tooltips,
  instance,
) {
  const datasets = new List()
    .setSize(types.length)
    .map((_v, index) =>
      pipe(
        () => ({
          label: titles[index],
          data: data[index],
          backgroundColor: colors[index],
          hoverBackgroundColor: hoverColors[index],
        }),
        (config) => {
          if (types[index] === 'line') {
            return merge(config, {
              type: 'line',
              fill: false,
              borderColor: colors[index],
              lineTension: 0,
              borderWidth: 3,
              yAxisID: 'total',
            });
          }
          return merge(config, { type: 'bar', yAxisID: 'changes' });
        },
      )(),
    )
    .toJS();
  const chartData = {
    labels,
    datasets,
  };

  if (instance) {
    instance.destroy();
  }

  return new Chart(canvas, {
    type: 'line',
    data: chartData,
    options: {
      tooltips: {
        bodySpacing: 5,
        callbacks: {
          title: (tooltipItem) => {
            const index = get(tooltipItem, [0, 'index']);
            if (tooltips) {
              return tooltips[index];
            }
            return get(tooltipItem, [0, 'xLabel']);
          },
        },
        intersect: false,
        mode: 'index',
        position: 'nearest',
      },
      legend: {
        position: 'bottom',
        fullWidth: true,
        labels: {
          boxWidth: 12,
        },
        onHover(e) {
          e.target.style.cursor = 'pointer';
        },
        onClick: () => {},
      },
      hover: {
        onHover(e) {
          const point = this.getElementAtEvent(e);
          if (point.length && get(point, [0, '_index']) > 0) {
            e.target.style.cursor = 'pointer';
          } else {
            e.target.style.cursor = 'default';
          }
        },
      },
      animation: {
        duration: animationEnabled ? 1500 : 0,
        easing: 'easeInOutExpo',
      },
      responsive: false,
      maintainAspectRatio: false,
      datasets: {
        bar: {
          barThickness: 8,
          categoryPercentage: 0.5,
        },
      },
      scales: {
        xAxes: [
          {
            id: 'bars',
            offset: true,
            gridLines: {
              display: false,
              offsetGridLines: false,
            },
            ticks: { maxTicksLimit: 0 },
          },
        ],
        yAxes: [
          {
            display: true,
            position: 'left',
            type: 'linear',
            ticks: {
              beginAtZero: true,
            },
            scaleLabel: {
              display: true,
              labelString: get(msg, 'axeTotal', ''),
            },
            gridLines: {
              display: true,
              drawBorder: false,
            },
            id: 'total',
          },
          {
            scaleLabel: {
              display: true,
              labelString: get(msg, 'axeChanges', ''),
            },
            display: true,
            type: 'linear',
            position: 'right',
            gridLines: {
              display: false,
            },
            ticks: {
              beginAtZero: true,
              precision: 0,
            },
            id: 'changes',
          },
        ],
      },
    },
  });
}
